import { useEffect, useState } from 'react';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';
import { Button } from '~/components/button';
import { useCopy } from '~/hooks';
import { fetchExistingUtilityAccounts } from '~/services';
import { fetchFunnelPacket } from '~/services/fetch-funnel-packet';
import { updateUser } from '~/services/update-user-meta-data';
import { BillingDataSource } from '~/typings/context';
import { AsyncReturnType } from '~/typings/utils';
import { IS_TEST, CALENDLY_MEET_LINK } from '~/utils/constants';
import { sendToSegment, trackError } from '~/utils/tracking';
import {
  OneItemGrid,
  CalendlyButtonWraper,
  CalendlyTtile,
  CalendlyColumn,
} from './calendly.styles';
import {
  style,
  pageSettings,
  fetchApiforStartTime,
  fetchApiforBuisnessName,
} from './calendly.utils';

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

export const Calendly = ({ context, next, overrideCopy }: CalendlyProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const { email, firstName, funnelPacket } = context;
  const { user } = funnelPacket;
  const prefill = { email, name: firstName };
  const schedule = !IS_TEST;
  const [isMeetingScheduled, setIsMeetingScheduled] = useState(schedule);
  const [inviteeUri, setInviteeUri] = useState('');
  const [eventUri, setEventUri] = useState('');
  const [buisnessName, setbuisnessName] = useState('');
  const [scheduledStartTime, setscheduledStartTime] = useState('');
  const [scheduledStartDate, setscheduledStartDate] = useState('');

  const fetchBuisnessNameForSegment = async uri => {
    try {
      const buisnessName = await fetchApiforBuisnessName(uri);
      setbuisnessName(buisnessName);
    } catch (err) {
      trackError(new Error(err.title), {
        extras: { message: err.message || err.title, details: err.details },
      });
    }
  };

  const fetchEventTimeForSegment = async uri => {
    try {
      const time = await fetchApiforStartTime(uri);
      const startTime = new Date(time);
      setscheduledStartDate(dateFormatter.format(startTime));
      setscheduledStartTime(timeFormatter.format(startTime));
    } catch (err) {
      trackError(new Error(err.title), {
        extras: { message: err.message || err.title, details: err.details },
      });
    }
  };

  useEffect(() => {
    if (inviteeUri !== '') fetchBuisnessNameForSegment(inviteeUri);
  }, [inviteeUri]);

  useEffect(() => {
    if (eventUri !== '') fetchEventTimeForSegment(eventUri);
  }, [eventUri]);

  useEffect(() => {
    const fetchData = async () => {
      if (scheduledStartTime !== '' && scheduledStartDate !== '') {
        const { utilityAccounts } = await fetchExistingUtilityAccounts({
          userId: user.id,
        });
        const utilityAccount = utilityAccounts?.find(
          ua => ua.id === context.funnelPacket?.utilityAccount.id,
        );
        updateUser(user.id, {
          meetingDetails: {
            scheduledTime: scheduledStartTime,
            scheduledDate: scheduledStartDate,
          },
        });
        /* eslint-disable camelcase */
        sendToSegment({
          type: 'welcome_call_scheduled',
          funnelPacket,
          additional: {
            buisnessName,
            scheduled_time: scheduledStartTime,
            scheduled_date: scheduledStartDate,
            account_number: utilityAccount?.accountNumber || null,
          },
        });
        /* eslint-enable camelcase */
      }
    };

    fetchData();
  }, [buisnessName, scheduledStartTime, scheduledStartDate]);

  useCalendlyEventListener({
    onEventScheduled: e => {
      setIsMeetingScheduled(false);
      setInviteeUri(e.data.payload.invitee.uri);
      setEventUri(e.data.payload.event.uri);
    },
  });

  return (
    <CalendlyColumn testId="screen-Calendly">
      <CalendlyTtile>{copy.title}</CalendlyTtile>
      <InlineWidget
        url={CALENDLY_MEET_LINK}
        styles={style}
        prefill={prefill}
        pageSettings={pageSettings}
      />
      <OneItemGrid>
        <CalendlyButtonWraper>
          <Button
            mode="primary"
            onClick={next}
            disabled={isMeetingScheduled}
            loading={false}
            testId="submit-calendy-schedule"
          >
            {copy.next}
          </Button>
        </CalendlyButtonWraper>
      </OneItemGrid>
    </CalendlyColumn>
  );
};

export const defaultCopy = () => {
  return {
    title: 'Schedule your welcome call',
    next: 'Continue',
  };
};

export type CalendlyCopy = ReturnType<typeof defaultCopy>;

export interface CalendlyProps {
  context: CalendlyContext;
  next: () => void;
  overrideCopy?: (context: CalendlyContext) => Partial<CalendlyCopy>;
}

export interface CalendlyContext {
  billingSelection?: BillingDataSource;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string | null | undefined;
  funnelPacket: AsyncReturnType<typeof fetchFunnelPacket>;
  utmParams?: Record<string, string | number>;
}
